import React, { useState } from "react";
import { Loader2, CheckCircle2, Layers, Monitor, Palette } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Plan {
  overview: string;
  features: string[];
  screens: string[];
  design_direction: string;
}

interface PlanReadyCardProps {
  plan: Plan;
  onApprove: () => Promise<void>;
  onSkip: () => Promise<void>;
}

export function PlanReadyCard({
  plan,
  onApprove,
  onSkip,
}: PlanReadyCardProps) {
  const [approving, setApproving] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const handleApprove = async () => {
    if (approving || skipping) return;
    setApproving(true);
    try {
      await onApprove();
    } finally {
      setApproving(false);
    }
  };

  const handleSkip = async () => {
    if (approving || skipping) return;
    setSkipping(true);
    try {
      await onSkip();
    } finally {
      setSkipping(false);
    }
  };

  return (
    <Card className="border-primary/30 bg-card">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <CheckCircle2 className="w-4 h-4 text-primary" />
          </div>
          <CardTitle className="text-lg font-medium text-foreground">
            Here's the plan
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overview */}
        <div>
          <p className="text-sm text-foreground leading-relaxed">
            {plan.overview}
          </p>
        </div>

        {/* Features */}
        {plan.features.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Features</h3>
            </div>
            <ul className="space-y-1.5 pl-6">
              {plan.features.map((feature, idx) => (
                <li
                  key={idx}
                  className="text-sm text-muted-foreground list-disc"
                >
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Screens */}
        {plan.screens.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Monitor className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Screens</h3>
            </div>
            <ul className="space-y-1.5 pl-6">
              {plan.screens.map((screen, idx) => (
                <li
                  key={idx}
                  className="text-sm text-muted-foreground list-disc"
                >
                  {screen}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Design Direction */}
        {plan.design_direction && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Design</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed pl-6">
              {plan.design_direction}
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={handleApprove}
            disabled={approving || skipping}
            className="flex-1"
          >
            {approving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Build this"
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={approving || skipping}
            className="text-muted-foreground"
          >
            {skipping ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Skip plan, build now"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
